// minimap/draw — pure ritmatte för Minimap (#7 i PRE_RELEASE_REVIEW_100).
// Extraherad ur Minimap.tsx så skalning, world→minimap-mappning,
// viewport-rektangeln och click→canvas-mappningen är enhets-testbara
// utan att mounta komponenten.

export interface ViewportRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Uniform skala som passar hela canvasen i minimap-ytan. */
export function computeMinimapScale(
  minimapWidth: number,
  minimapHeight: number,
  canvasWidth: number,
  canvasHeight: number,
): number {
  return Math.min(minimapWidth / canvasWidth, minimapHeight / canvasHeight);
}

/**
 * World→canvas-CSS-mappning (före minimap-skalning). Block-koordinater
 * är center-origin (CLAUDE.md-konventionen); canvasens CSS-rymd har
 * origo i övre vänstra hörnet med world (0,0) i mitten.
 */
export function makeWorldToCanvas(zoom: number, canvasWidth: number, canvasHeight: number) {
  return {
    toScreenX: (logicalX: number) => logicalX * zoom + canvasWidth / 2,
    toScreenY: (logicalY: number) => logicalY * zoom + canvasHeight / 2,
    toScreenSize: (size: number) => size * zoom,
  };
}

/**
 * Viewport-indikatorns rektangel i minimap-pixlar. Clampas så den
 * aldrig sticker utanför minimapen; min-storlek 4 px så indikatorn
 * är synlig även för enorma canvases.
 */
export function computeViewportRect(args: {
  scrollLeft: number;
  scrollTop: number;
  viewportWidth: number;
  viewportHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  scale: number;
  minimapWidth: number;
  minimapHeight: number;
}): ViewportRect {
  const {
    scrollLeft, scrollTop, viewportWidth, viewportHeight,
    canvasWidth, canvasHeight, scale, minimapWidth, minimapHeight,
  } = args;
  const scrollFractionX = canvasWidth > 0 ? scrollLeft / canvasWidth : 0;
  const scrollFractionY = canvasHeight > 0 ? scrollTop / canvasHeight : 0;
  const viewportFractionW = canvasWidth > 0 ? Math.min(1, viewportWidth / canvasWidth) : 1;
  const viewportFractionH = canvasHeight > 0 ? Math.min(1, viewportHeight / canvasHeight) : 1;

  const minimapCanvasWidth = canvasWidth * scale;
  const minimapCanvasHeight = canvasHeight * scale;

  const rawX = scrollFractionX * minimapCanvasWidth;
  const rawY = scrollFractionY * minimapCanvasHeight;
  const rawWidth = Math.max(4, viewportFractionW * minimapCanvasWidth);
  const rawHeight = Math.max(4, viewportFractionH * minimapCanvasHeight);

  return {
    x: Math.max(0, Math.min(rawX, minimapWidth - rawWidth)),
    y: Math.max(0, Math.min(rawY, minimapHeight - rawHeight)),
    width: Math.min(rawWidth, minimapWidth),
    height: Math.min(rawHeight, minimapHeight),
  };
}

/** Minimap-klick (i minimap-pixlar relativt elementet) → canvas-CSS-koordinat. */
export function minimapClickToCanvas(
  offsetX: number,
  offsetY: number,
  scale: number,
): { x: number; y: number } {
  return { x: offsetX / scale, y: offsetY / scale };
}
